import type { ProductOptionDTO } from "@/modules/options/domain/contracts";
import type {
    ProductCreateInput,
    ProductUpdateInput,
} from "@/modules/products/domain/contracts";
import { config } from "@/shared/lib/config";
import { slugify } from "@/shared/lib/slugify";
import { fetchLookupForInfinite, hydrateLookupItems } from "@/shared/lib/utils";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import {
    Badge,
    Button,
    FormInput,
    FormRoot,
    Label,
    RHF,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    cn,
    zodResolver,
} from "@devhop/ui";
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { ProductKindRadio } from "./ProductKindRadio";
import { ShowcaseServiceSlugsField } from "./ShowcaseServiceSlugsField";
import {
  getProductCatalogConfig,
  type ProductCatalogMode,
} from "./product-catalog-config";
import { productKindToFlags } from "./product-kind";
import { TagInput } from "./TagInput";

const ProductFormSchema = z
  .object({
    nameLo: z.string().min(1, "ຕ້ອງໃສ່ຊື່ (ລາວ)"),
    nameTh: z.string().optional(),
    nameEn: z.string().optional(),
    slug: z.string().min(1, "ຕ້ອງໃສ່ slug"),
    descriptionLo: z.string().optional(),
    descriptionTh: z.string().optional(),
    descriptionEn: z.string().optional(),
    status: z.enum(["draft", "active", "archived"]).default("draft"),
    kind: z
      .enum(["retail", "production", "hybrid", "online-showcase"])
      .default("retail"),
    showcaseServiceSlugs: z.array(z.string()).default([]),
    tags: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          slug: z.string(),
        }),
      )
      .default([]),
    categoryIds: z.array(z.string()).default([]),
    collectionIds: z.array(z.string()).default([]),
    optionIds: z.array(z.string()).default([]),
    optionValueLinks: z.record(z.string(), z.array(z.string())).default({}),
  })
  .superRefine((data, ctx) => {
    if (
      (data.kind === "production" || data.kind === "online-showcase") &&
      data.showcaseServiceSlugs.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 ບໍລິການ",
        path: ["showcaseServiceSlugs"],
      });
    }
    for (const optionId of data.optionIds) {
      const values = data.optionValueLinks[optionId] ?? [];
      if (values.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງເລືອກຄ່າຕົວເລືອກຢ່າງໜ້ອຍ 1 ຄ່າ",
          path: ["optionValueLinks", optionId],
        });
      }
    }
  });

export type ProductFormValues = z.output<typeof ProductFormSchema>;

const DEV_FILL: ProductFormValues = {
  nameLo: "ເສື້ອເຊີດຜ້າຄໍຕອນ",
  nameTh: "เสื้อเชิ้ตผ้าคอตตอน",
  nameEn: "Cotton Shirt",
  slug: "cotton-shirt",
  descriptionLo: "ເສື້ອເຊີດຜ້າຄໍຕອນ 100% ຄຸນນະພາບດີ ສະດວກສະບາຍ",
  descriptionTh: "เสื้อเชิ้ตผ้าคอตตอน 100% คุณภาพดี สวมใส่สบาย",
  descriptionEn: "100% Cotton shirt, high quality, comfortable wear",
  status: "active",
  kind: "retail",
  tags: [],
  categoryIds: [],
  collectionIds: [],
  optionIds: [],
  optionValueLinks: {},
  showcaseServiceSlugs: [],
};

type ProductFormProps = {
  initialValues?: Partial<ProductFormValues>;
  onSubmit: (values: ProductCreateInput | ProductUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
  optionOptions?: Array<ProductOptionDTO>;
  catalogMode?: ProductCatalogMode;
};

export function ProductForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
  optionOptions = [],
  catalogMode = "retail",
}: ProductFormProps) {
  const catalogConfig = getProductCatalogConfig(catalogMode);
  const lockedKind = catalogConfig.lockedKind;
  const [activeTab, setActiveTab] = useState<"lo" | "th" | "en">("lo");

  const methods = RHF.useForm<ProductFormValues>({
    resolver: zodResolver(ProductFormSchema) as any,
    defaultValues: {
      nameLo: "",
      nameTh: "",
      nameEn: "",
      slug: "",
      descriptionLo: "",
      descriptionTh: "",
      descriptionEn: "",
      status: "draft",
      kind: lockedKind ?? "retail",
      tags: [],
      categoryIds: [],
      collectionIds: [],
      optionIds: [],
      optionValueLinks: {},
      showcaseServiceSlugs: [],
      ...initialValues,
      ...(lockedKind ? { kind: lockedKind } : {}),
    },
  });

  const nameLo = methods.watch("nameLo");
  const nameEn = methods.watch("nameEn");
  const selectedOptionIds = methods.watch("optionIds") || [];
  const optionValueLinks: Record<string, string[]> =
    methods.watch("optionValueLinks") || {};

  // Auto-generate slug from name
  useEffect(() => {
    if (!initialValues?.slug && (nameLo || nameEn)) {
      const slugSource = nameEn || nameLo;
      methods.setValue("slug", slugify(slugSource));
    }
  }, [nameLo, nameEn, initialValues?.slug, methods]);

  const handleFormSubmit = (values: ProductFormValues) => {
    const kind = lockedKind ?? values.kind;
    const flags = productKindToFlags(kind);
    const input: ProductCreateInput | ProductUpdateInput = {
      name: {
        lo: values.nameLo,
        th: values.nameTh,
        en: values.nameEn,
      },
      slug: values.slug,
      description: {
        lo: values.descriptionLo,
        th: values.descriptionTh,
        en: values.descriptionEn,
      },
      status: values.status,
      kind,
      isSellable: flags.isSellable,
      isShowcaseEligible: flags.isShowcaseEligible,
      showcaseServiceSlugs: catalogConfig.requiresShowcaseServices
        ? values.showcaseServiceSlugs
        : [],
      tagIds: catalogConfig.simplifiedForm
        ? []
        : values.tags.map((t) => t.id),
      categoryIds: catalogConfig.simplifiedForm ? [] : values.categoryIds,
      collectionIds: catalogConfig.simplifiedForm ? [] : values.collectionIds,
      optionIds: catalogConfig.hideOptionsInForm ? [] : values.optionIds,
      optionValueLinks: catalogConfig.hideOptionsInForm
        ? {}
        : values.optionValueLinks,
    };
    onSubmit(input);
  };

  const getDisplayName = (
    name: { lo?: string; en?: string; th?: string } | string | undefined,
  ): string => {
    if (typeof name === "string") return name;
    if (!name) return "";
    return name.lo || name.en || name.th || "";
  };

  const formatOptionLabel = (option: ProductOptionDTO) => {
    const name = getDisplayName(option.name);
    const slug = option.slug?.trim();
    if (!slug) return name;
    return name ? `${name} · ${slug}` : slug;
  };

  const fillDev = () => {
    methods.reset(DEV_FILL);
    if (onDevFill) onDevFill();
  };

  // Add option to product
  const handleAddOption = (optionId: string) => {
    if (!selectedOptionIds.includes(optionId)) {
      methods.setValue("optionIds", [...selectedOptionIds, optionId]);
      // Initialize empty value links for this option
      if (!optionValueLinks[optionId]) {
        methods.setValue("optionValueLinks", {
          ...optionValueLinks,
          [optionId]: [],
        });
      }
    }
  };

  // Remove option from product
  const handleRemoveOption = (optionId: string) => {
    methods.setValue(
      "optionIds",
      selectedOptionIds.filter((id: string) => id !== optionId),
    );
    const newLinks = { ...optionValueLinks };
    delete newLinks[optionId];
    methods.setValue("optionValueLinks", newLinks);
  };

  // Add value to option
  const handleAddValue = (optionId: string, valueId: string) => {
    const currentValues = optionValueLinks[optionId] || [];
    if (!currentValues.includes(valueId)) {
      methods.setValue("optionValueLinks", {
        ...optionValueLinks,
        [optionId]: [...currentValues, valueId],
      });
    }
  };

  // Remove value from option
  const handleRemoveValue = (optionId: string, valueId: string) => {
    const currentValues = optionValueLinks[optionId] || [];
    methods.setValue("optionValueLinks", {
      ...optionValueLinks,
      [optionId]: currentValues.filter((id: string) => id !== valueId),
    });
  };

  return (
    <FormRoot<ProductFormValues>
      methods={methods as UseFormReturn<ProductFormValues>}
      onSubmit={handleFormSubmit}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium text-lg">
          {catalogConfig.formLabels.sectionTitle}
        </h3>
        {onDevFill && (
          <Button type="button" variant="outline" size="sm" onClick={fillDev}>
            ເຕີມຂໍ້ມູນທົດສອບ
          </Button>
        )}
      </div>
      <div
        className={
          catalogConfig.simplifiedForm
            ? "mx-auto max-w-3xl space-y-6"
            : "grid grid-cols-1 gap-6 lg:grid-cols-3"
        }
      >
        {/* Main fields */}
        <div
          className={
            catalogConfig.simplifiedForm ? "space-y-6" : "space-y-6 lg:col-span-2"
          }
        >
          {/* Language Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as "lo" | "th" | "en")}
          >
            <TabsList
              className="grid w-full max-w-md grid-cols-3"
              data-tourid="product-form-tabs"
            >
              <TabsTrigger value="lo">ລາວ (LO)</TabsTrigger>
              <TabsTrigger value="th">ໄທ (TH)</TabsTrigger>
              <TabsTrigger value="en">ອັງກິດ (EN)</TabsTrigger>
            </TabsList>

            <TabsContent value="lo" className="mt-4 space-y-4">
              <FormInput
                name="nameLo"
                label="ຊື່ສິນຄ້າ"
                placeholder="ເສື້ອເຊີດຜ້າຄໍຕອນ"
                required
                data-tourid="product-form-name-lo"
              />
              <div data-tourid="product-form-description">
                <Label htmlFor="descriptionLo">ລາຍລະອຽດ</Label>
                <textarea
                  id="descriptionLo"
                  {...methods.register("descriptionLo")}
                  placeholder="ອະທິບາຍລາຍລະອຽດສິນຄ້າແຟຊັ່ນ..."
                  rows={6}
                  className="mt-1.5 flex min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </TabsContent>

            <TabsContent value="th" className="mt-4 space-y-4">
              <FormInput
                name="nameTh"
                label="ຊື່ (ไทย)"
                placeholder="เสื้อเชิ้ตผ้าคอตตอน"
              />
              <div>
                <Label htmlFor="descriptionTh">ລາຍລະອຽດ (ໄທ)</Label>
                <textarea
                  id="descriptionTh"
                  {...methods.register("descriptionTh")}
                  placeholder="ອະທິບາຍລາຍລະອຽດສິນຄ້າແຟຊັ່ນ..."
                  rows={6}
                  className="mt-1.5 flex min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </TabsContent>

            <TabsContent value="en" className="mt-4 space-y-4">
              <FormInput
                name="nameEn"
                label="ຊື່ສິນຄ້າ (ອັງກິດ)"
                placeholder="Cotton Shirt"
              />
              <div>
                <Label htmlFor="descriptionEn">ລາຍລະອຽດ (ອັງກິດ)</Label>
                <textarea
                  id="descriptionEn"
                  {...methods.register("descriptionEn")}
                  placeholder="ອະທິບາຍລາຍລະອຽດສິນຄ້າແຟຊັ່ນ..."
                  rows={6}
                  className="mt-1.5 flex min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </TabsContent>
          </Tabs>

          {/* Slug */}
          <FormInput
            name="slug"
            label="Slug (ທີ່ຢູ່ URL)"
            placeholder="cotton-shirt"
            hint="ຖືກສ້າງອັດຕະໂນມັດຈາກຊື່ອັງກິດ, ແກ້ໄຂໄດ້"
            required
            data-tourid="product-form-slug"
          />

          {/* Options Section */}
          {!catalogConfig.hideOptionsInForm && (
          <div
            className="rounded-lg border p-4"
            data-tourid="product-form-options"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold">ຕົວເລືອກ</h3>
              <Select value="" onValueChange={handleAddOption}>
                <SelectTrigger className="w-50">
                  <SelectValue placeholder="+ ເພີ່ມຕົວເລືອກ" />
                </SelectTrigger>
                <SelectContent>
                  {optionOptions
                    .filter((opt) => !selectedOptionIds.includes(opt.id))
                    .map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {formatOptionLabel(option)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {selectedOptionIds.length === 0 ? (
              <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີຕົວເລືອກ</p>
            ) : (
              <div className="space-y-4">
                {selectedOptionIds.map((optionId) => {
                  const option = optionOptions.find(
                    (opt) => opt.id === optionId,
                  );
                  if (!option) return null;
                  const selectedValues = optionValueLinks[optionId] || [];

                  const optionError =
                    methods.formState.errors?.optionValueLinks?.[option.id];

                  return (
                    <div
                      key={option.id}
                      className={cn(
                        "rounded-md border p-3",
                        optionError && "border-destructive",
                      )}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="font-medium">
                          {formatOptionLabel(option)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(option.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Hint: ต้องเลือก option value */}
                      {selectedValues.length === 0 && (
                        <p className="mb-2 text-destructive text-sm">
                          ຕ້ອງເລືອກຄ່າຕົວເລືອກຢ່າງໜ້ອຍ 1 ຄ່າສຳລັບສິນຄ້າ
                        </p>
                      )}

                      {/* Option values as badge-style checkboxes */}
                      {option.values && option.values.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {option.values.map((value) => {
                            const isSelected = selectedValues.includes(
                              value.id,
                            );
                            return (
                              <label
                                key={value.id}
                                className="cursor-pointer"
                                htmlFor={`option-value-${option.id}-${value.id}`}
                              >
                                <input
                                  id={`option-value-${option.id}-${value.id}`}
                                  type="checkbox"
                                  className="peer sr-only"
                                  checked={isSelected}
                                  onChange={() => {
                                    if (isSelected) {
                                      handleRemoveValue(option.id, value.id);
                                    } else {
                                      handleAddValue(option.id, value.id);
                                    }
                                  }}
                                  aria-label={getDisplayName(value.value)}
                                />
                                <Badge
                                  variant={isSelected ? "secondary" : "outline"}
                                  className={cn(
                                    "inline-flex cursor-pointer items-center gap-1.5 px-3 py-1.5 transition-colors",
                                    "hover:bg-secondary/80 hover:text-secondary-foreground",
                                    "peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
                                    isSelected &&
                                      "border-primary bg-primary/10 text-primary",
                                  )}
                                >
                                  {isSelected && (
                                    <Check className="h-3.5 w-3.5 shrink-0" />
                                  )}
                                  {getDisplayName(value.value)}
                                </Badge>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">
                          ບໍ່ມີຄ່າຕົວເລືອກ ກະລຸນາເພີ່ມຄ່າໃນໜ້າຕັ້ງຄ່າ Option
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {catalogConfig.requiresShowcaseServices && (
            <ShowcaseServiceSlugsField disabled={submitting} />
          )}

          {catalogConfig.simplifiedForm && (
            <div className="space-y-2" data-tourid="product-form-status">
              <Label>ສະຖານະ</Label>
              <RHF.Controller
                control={methods.control}
                name="status"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">ແບບຮ່າງ</SelectItem>
                      <SelectItem value="active">ເປີດໃຊ້ງານ</SelectItem>
                      <SelectItem value="archived">ເກັບຖາວອນ</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          )}

          {catalogConfig.formLabels.imagesHint && (
            <p className="text-muted-foreground text-sm">
              {catalogConfig.formLabels.imagesHint}
            </p>
          )}

          {catalogConfig.simplifiedForm && (
            <div
              className="flex justify-end gap-2"
              data-tourid="product-form-submit"
            >
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={submitting}
                >
                  ຍົກເລີກ
                </Button>
              )}
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? "ກຳລັງບັນທຶກ..."
                  : catalogConfig.formLabels.submit}
              </Button>
            </div>
          )}
        </div>

        {!catalogConfig.simplifiedForm && (
        <div className="space-y-6">
          <div className="rounded-lg border p-4">
            <h3 className="mb-4 font-semibold">ຈັດກຸ່ມ</h3>

            {/* Category */}
            <div className="mb-4 space-y-2" data-tourid="product-form-category">
              <FormInfiniteMultiCombobox<{
                id: string;
                name: { lo?: string; th?: string; en?: string };
              }>
                name="categoryIds"
                label="ຫມວດມູ່"
                queryKey={["categories", "lookup"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/categories/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(ids) =>
                  hydrateLookupItems(`${config.apiUrl}/categories/lookup`, ids)
                }
                getLabel={(item) => getDisplayName(item.name)}
                getValue={(item) => item.id}
                placeholder="ຄົ້ນຫາ ຫມວດມູ່..."
                clearable
              />
            </div>

            {/* Collections */}
            <div
              className="mb-4 space-y-2"
              data-tourid="product-form-collections"
            >
              <FormInfiniteMultiCombobox<{
                id: string;
                name: { lo?: string; th?: string; en?: string };
              }>
                name="collectionIds"
                label="ຄໍເລັກຊັນ"
                queryKey={["collections", "lookup"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/catalog/collections/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(ids) =>
                  hydrateLookupItems(
                    `${config.apiUrl}/catalog/collections/lookup`,
                    ids,
                  )
                }
                getLabel={(item) => getDisplayName(item.name)}
                getValue={(item) => item.id}
                placeholder="ຄົ້ນຫາ ຄໍເລັກຊັນ..."
                clearable
              />
            </div>

            {/* Tags */}
            <div className="mb-4 space-y-2" data-tourid="product-form-tags">
              <Label>Tag</Label>
              <RHF.Controller
                control={methods.control}
                name="tags"
                render={({ field }) => (
                  <TagInput
                    value={field.value}
                    onChange={field.onChange}
                    disabled={submitting}
                  />
                )}
              />
              <p className="text-muted-foreground text-xs">
                ກົດ Enter ເພື່ອເພີ່ມ tag ໃໝ່
              </p>
            </div>

            {!lockedKind && (
              <div className="mb-4 space-y-2">
                <Label>ປະເພດສິນຄ້າ</Label>
                <RHF.Controller
                  control={methods.control}
                  name="kind"
                  render={({ field }) => (
                    <ProductKindRadio
                      value={field.value}
                      onChange={field.onChange}
                      disabled={submitting}
                    />
                  )}
                />
              </div>
            )}

            {/* Status */}
            <div className="space-y-2" data-tourid="product-form-status">
              <Label>ສະຖານະ</Label>
              <RHF.Controller
                control={methods.control}
                name="status"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">ແບບຮ່າງ</SelectItem>
                      <SelectItem value="active">ເປີດໃຊ້ງານ</SelectItem>
                      <SelectItem value="archived">ເກັບຖາວອນ</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-2"
            data-tourid="product-form-submit"
          >
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={submitting}
              >
                ຍົກເລີກ
              </Button>
            )}
            <Button type="submit" disabled={submitting}>
              {submitting
                ? "ກຳລັງບັນທຶກ..."
                : catalogConfig.formLabels.submit}
            </Button>
          </div>
        </div>
        )}
      </div>
    </FormRoot>
  );
}
